import React from 'react';
import DecimalInput from './DecimalInput';
import IntegerInput from './IntegerInput';
import FileAttachment from './FileAttachment';

const DEFAULT_COLUMN_TYPE = 'text';

const normalizeColumns = (columns) =>
  Array.isArray(columns)
    ? columns
        .map((column, index) => ({
          id: typeof column?.id === 'string' && column.id.trim() ? column.id.trim() : `coluna_${index + 1}`,
          label: typeof column?.label === 'string' && column.label.trim() ? column.label.trim() : `Coluna ${index + 1}`,
          type: typeof column?.type === 'string' && column.type ? column.type : DEFAULT_COLUMN_TYPE,
          required: Boolean(column?.required),
        }))
        .filter((column) => column.id && column.label)
    : [];

const createEmptyRow = (columns) => {
  const nextRow = {};
  columns.forEach((column) => {
    nextRow[column.id] = '';
  });
  return nextRow;
};

export default function TableField({
  field,
  value,
  onChange,
  readOnly = false,
  inputClassName = '',
  onUploadFile,
}) {
  const columns = normalizeColumns(field?.tableColumns);
  const rows = Array.isArray(value) ? value : [];

  const updateCell = (rowIndex, columnId, nextValue) => {
    const nextRows = [...rows];
    const existingRow = nextRows[rowIndex] && typeof nextRows[rowIndex] === 'object' ? nextRows[rowIndex] : {};
    nextRows[rowIndex] = {
      ...existingRow,
      [columnId]: nextValue,
    };
    onChange(nextRows);
  };

  const addRow = () => {
    onChange([...rows, createEmptyRow(columns)]);
  };

  const removeRow = (rowIndex) => {
    const nextRows = rows.filter((_, index) => index !== rowIndex);
    onChange(nextRows);
  };

  if (columns.length === 0) {
    return <p className="text-xs text-amber-600">Defina ao menos uma coluna para usar o campo de tabela.</p>;
  }

  return (
    <div className="space-y-2">
      <div className="overflow-x-auto rounded-md border border-slate-200">
        <table className="min-w-full divide-y divide-slate-200 text-sm">
          <thead className="bg-slate-50">
            <tr>
              {columns.map((column) => (
                <th key={column.id} className="whitespace-nowrap px-3 py-2 text-left font-semibold text-slate-700">
                  {column.label}
                  {column.required && <span className="text-red-500"> *</span>}
                </th>
              ))}
              {!readOnly && <th className="px-3 py-2 text-right font-semibold text-slate-700">Ações</th>}
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-200 bg-white">
            {rows.length === 0 && (
              <tr>
                <td colSpan={columns.length + (readOnly ? 0 : 1)} className="px-3 py-4 text-center text-xs text-slate-500">
                  Nenhuma linha adicionada.
                </td>
              </tr>
            )}
            {rows.map((row, rowIndex) => (
              <tr key={`table-row-${rowIndex}`}>
                {columns.map((column) => {
                  const cellValue = row?.[column.id] ?? '';
                  if (column.type === 'file') {
                    return (
                      <td key={`${rowIndex}-${column.id}`} className="px-3 py-2 align-top">
                        {readOnly ? (
                          <FileAttachment file={cellValue} />
                        ) : (
                          <div className="space-y-2">
                            {cellValue && <FileAttachment file={cellValue} />}
                            <input
                              type="file"
                              className="w-full cursor-pointer rounded-md border border-dashed border-slate-300 bg-white px-2 py-1 text-xs text-slate-700"
                              onChange={(event) => {
                                const selectedFile = event.target.files?.[0];
                                if (!selectedFile || !onUploadFile) return;
                                onUploadFile({ rowIndex, column, file: selectedFile });
                                event.target.value = '';
                              }}
                              required={column.required && !cellValue}
                            />
                          </div>
                        )}
                      </td>
                    );
                  }

                  if (column.type === 'number') {
                    return (
                      <td key={`${rowIndex}-${column.id}`} className="px-3 py-2 align-top">
                        <IntegerInput
                          value={cellValue}
                          onChange={(event) => updateCell(rowIndex, column.id, event.target.value)}
                          readOnly={readOnly}
                          required={column.required}
                          className={inputClassName}
                        />
                      </td>
                    );
                  }

                  if (column.type === 'decimal') {
                    return (
                      <td key={`${rowIndex}-${column.id}`} className="px-3 py-2 align-top">
                        <DecimalInput
                          decimalPlaces={2}
                          value={cellValue}
                          onChange={(event) => updateCell(rowIndex, column.id, event.target.value)}
                          readOnly={readOnly}
                          required={column.required}
                          className={inputClassName}
                        />
                      </td>
                    );
                  }

                  return (
                    <td key={`${rowIndex}-${column.id}`} className="px-3 py-2 align-top">
                      <input
                        type={column.type === 'date' ? 'date' : column.type === 'email' ? 'email' : 'text'}
                        value={cellValue}
                        onChange={(event) => updateCell(rowIndex, column.id, event.target.value)}
                        readOnly={readOnly}
                        required={column.required}
                        className={inputClassName}
                      />
                    </td>
                  );
                })}
                {!readOnly && (
                  <td className="px-3 py-2 text-right align-top">
                    <button
                      type="button"
                      onClick={() => removeRow(rowIndex)}
                      className="rounded border border-red-200 px-2 py-1 text-xs font-medium text-red-600 hover:bg-red-50"
                    >
                      Remover
                    </button>
                  </td>
                )}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      {!readOnly && (
        <button
          type="button"
          onClick={addRow}
          className="rounded border border-blue-200 px-3 py-1.5 text-xs font-semibold text-blue-600 hover:bg-blue-50"
        >
          Adicionar linha
        </button>
      )}
    </div>
  );
}
